import { useCallback } from 'react'

import { Typography } from '@mui/material'
import { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'

import { StatusBox } from 'app/lib/components/StatusBox'
import { CursorPaginatedTable } from 'app/lib/components/table'
import TableContent from 'app/lib/components/TableContent'
import { DEFAULT_PAGE_LIMIT } from 'app/lib/constants'
import { useIsSuperAdmin } from 'app/lib/hoc/withProtectedComponent'
import { formatDate } from 'app/lib/utils/date'
import { generateQueryParamsFromSearchQuery } from 'app/lib/utils/helpers'
import { GetEligibilityRecordsParams } from 'app/models/EligibilityRecord'
import { useGetEligibilityRecordsQuery } from 'app/redux/scribeApi'

export const Table = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isSuperAdmin = useIsSuperAdmin()

  const { t: tHeader } = useTranslation(undefined, {
    keyPrefix: 'eligibilityRecordListPage.eligibilityRecordsTable.headers',
  })

  const {
    t,
    i18n: { resolvedLanguage = 'en' },
  } = useTranslation()

  const { search, cursor } = Object.fromEntries(
    searchParams.entries(),
  ) as GetEligibilityRecordsParams
  const queryParams = generateQueryParamsFromSearchQuery(search || '')
  const { data, isFetching } = useGetEligibilityRecordsQuery({
    ...queryParams,
    cursor,
    include_trust_based: !!isSuperAdmin,
  })

  const records = data?.data || []
  const pagination = {
    nextCursor: data?.meta?.nextCursor,
    prevCursor: data?.meta?.prevCursor,
  }

  const onFetchData = useCallback(
    (csr?: string) => {
      const params: Record<string, string> = {}
      if (search) params.search = search
      if (csr) params.cursor = csr
      setSearchParams(params)
    },
    [setSearchParams, search],
  )

  const columns: GridColDef[] = [
    {
      field: 'uniqueIdentifier',
      headerName: tHeader('uniqueIdentifier'),
      width: 275,
    },
    {
      field: 'firstName',
      headerName: tHeader('firstName'),
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="subtitle2"
          title={params.value}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      headerName: tHeader('lastName'),
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="subtitle2"
          title={params.value}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'organizations',
      headerName: tHeader('organizations'),
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="subtitle2"
          title={params.value}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'dateOfBirth',
      headerName: tHeader('dateOfBirth'),
      flex: 1,
      renderCell: (params) => (params.value ? formatDate(params.value, resolvedLanguage) : ''),
    },
    {
      field: 'activationDate',
      headerName: tHeader('activationDate'),
      flex: 1,
      renderCell: (params) => formatDate(params.value, resolvedLanguage),
    },
    {
      field: 'deactivationDate',
      headerName: tHeader('deactivationDate'),
      flex: 1,
      renderCell: (params) => formatDate(params.value, resolvedLanguage),
    },
    {
      field: 'status',
      headerName: tHeader('status'),
      width: 100,
      renderCell: (params) => <StatusBox status={params.value} />,
    },
  ]

  const rows = records.map(
    ({ id, organizationId, attributes, eligibleIntervals, status, organization }) => ({
      id,
      uniqueIdentifier: attributes.uniqueIdentifier,
      firstName: attributes.firstName,
      lastName: attributes.lastName,
      organizations: organization?.name,
      dateOfBirth: attributes.dateOfBirth,
      activationDate: eligibleIntervals[eligibleIntervals.length - 1].startDate,
      deactivationDate: eligibleIntervals[eligibleIntervals.length - 1].endDate,
      status,
      organizationId,
    }),
  )

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/organizations/${params.row.organizationId}/members/${params.row.id}`)
  }

  return (
    <TableContent
      isLoading={isFetching}
      count={records.length}
      notFound={t('eligibilityRecordListPage.eligibilityRecordsTable.noMemberFound')}
      searchNoMatch={t('eligibilityRecordListPage.eligibilityRecordsTable.searchNoMatch')}
      iconName="no_member_found"
    >
      <CursorPaginatedTable
        key={search}
        rows={rows}
        columns={columns}
        limit={DEFAULT_PAGE_LIMIT}
        pagination={pagination}
        onFetchData={onFetchData}
        onRowClick={handleRowClick}
        testID="eligibility-record-table"
      />
    </TableContent>
  )
}
